import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle2, Circle, ChevronDown, ChevronUp, X,
  Share2, PenSquare, CalendarDays, Users, Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Step {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  checkFn: () => Promise<boolean>;
  action?: () => void;
  actionLabel?: string;
}

interface Props {
  onNavigate: (view: string) => void;
}

const DISMISSED_KEY = "onboarding_checklist_dismissed_v1";

export function OnboardingChecklist({ onNavigate }: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISSED_KEY) === "1"; } catch { return false; }
  });
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const [checking, setChecking] = useState(false);

  const steps: Step[] = [
    {
      id: "connect_account",
      label: "Connect a social account",
      description: "Link Instagram, Twitter, LinkedIn, Facebook, or Threads",
      icon: Share2,
      checkFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;
        const { count } = await supabase.from("social_accounts").select("id", { count: "exact", head: true }).eq("user_id", user.id).eq("is_connected", true);
        return (count ?? 0) > 0;
      },
      action: () => onNavigate("social-hub"),
      actionLabel: "Connect",
    },
    {
      id: "create_post",
      label: "Create your first post",
      description: "Compose and publish or schedule a post",
      icon: PenSquare,
      checkFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;
        const { count } = await (supabase as any).from("posts").select("id", { count: "exact", head: true }).eq("user_id", user.id);
        return (count ?? 0) > 0;
      },
      action: () => onNavigate("social-hub"),
      actionLabel: "Compose",
    },
    {
      id: "schedule_post",
      label: "Schedule content ahead",
      description: "Set a future publish time for a post",
      icon: CalendarDays,
      checkFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;
        const { count } = await (supabase as any).from("posts").select("id", { count: "exact", head: true }).eq("user_id", user.id).eq("status", "scheduled");
        return (count ?? 0) > 0;
      },
      action: () => onNavigate("social-hub"),
      actionLabel: "Schedule",
    },
    {
      id: "invite_team",
      label: "Invite a team member",
      description: "Add colleagues to collaborate on content",
      icon: Users,
      checkFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;
        const { count } = await (supabase as any).from("organization_members").select("id", { count: "exact", head: true }).neq("user_id", user.id);
        return (count ?? 0) > 0;
      },
      action: () => onNavigate("org-users"),
      actionLabel: "Invite",
    },
    {
      id: "ai_generate",
      label: "Generate AI content",
      description: "Use AI to write or optimise a caption",
      icon: Sparkles,
      checkFn: async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;
        const { count } = await (supabase as any).from("posts").select("id", { count: "exact", head: true }).eq("user_id", user.id).eq("is_ai_generated", true);
        return (count ?? 0) > 0;
      },
      action: () => onNavigate("social-hub"),
      actionLabel: "Try AI",
    },
  ];

  const runChecks = async () => {
    setChecking(true);
    const results = await Promise.all(steps.map(async (s) => ({ id: s.id, done: await s.checkFn() })));
    setCompletedIds(new Set(results.filter(r => r.done).map(r => r.id)));
    setChecking(false);
  };

  useEffect(() => {
    if (!dismissed) runChecks();
  }, []);

  if (dismissed) return null;

  const doneCount = completedIds.size;
  const total = steps.length;
  const allDone = doneCount === total;
  const pct = Math.round((doneCount / total) * 100);

  return (
    <div className="fixed bottom-5 right-5 z-50 w-80 bg-card border border-border rounded-2xl shadow-xl overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center gap-2 px-4 py-3 cursor-pointer select-none bg-muted/40 hover:bg-muted/60 transition-colors"
        onClick={() => setOpen(p => !p)}
      >
        <Sparkles className="w-4 h-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-none">Getting started</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{doneCount}/{total} steps complete</p>
        </div>
        <Badge variant={allDone ? "default" : "secondary"} className="text-[10px] shrink-0">
          {pct}%
        </Badge>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />}
        <button
          onClick={e => { e.stopPropagation(); localStorage.setItem(DISMISSED_KEY, "1"); setDismissed(true); }}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {open && (
        <div className="px-4 pb-4 space-y-3 pt-2">
          {/* Progress bar */}
          <Progress value={pct} className="h-1.5" />

          {allDone ? (
            <div className="text-center py-3">
              <CheckCircle2 className="w-8 h-8 text-primary mx-auto mb-1" />
              <p className="text-sm font-medium">All steps complete!</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">You're ready to manage your social presence.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {steps.map(step => {
                const done = completedIds.has(step.id);
                const Icon = step.icon;
                return (
                  <li key={step.id} className={cn("flex items-start gap-2.5 p-2 rounded-lg transition-colors", done ? "opacity-50" : "hover:bg-muted/40")}>
                    {done
                      ? <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      : <Circle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                    }
                    <div className="flex-1 min-w-0">
                      <p className={cn("text-xs font-medium leading-snug", done && "line-through")}>{step.label}</p>
                      {!done && <p className="text-[10px] text-muted-foreground mt-0.5">{step.description}</p>}
                    </div>
                    {!done && step.action && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-[11px] text-primary hover:text-primary shrink-0"
                        onClick={step.action}
                      >
                        {step.actionLabel}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs text-muted-foreground h-7"
            onClick={runChecks}
            disabled={checking}
          >
            {checking ? "Checking…" : "Refresh status"}
          </Button>
        </div>
      )}
    </div>
  );
}
